// src/pages/ODDIndicators.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Container,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Button,
    Card,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiCardContent from '@mui/material/CardContent';
import Footer from '../navbar/Footer';
import { getPublications } from '../services/publicationService';

const CardContent = styled(MuiCardContent)(`
  display: flex;
  flex-direction: row;
  padding: 16px;
`);

function ODDIndicators() {
    const [oddData, setOddData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [visibleCount, setVisibleCount] = useState(2);
    const [filterYear, setFilterYear] = useState('all');

    // Chargement des données ODD depuis l'API
    useEffect(() => {
        setLoading(true);
        getPublications({ type: 'iodd', perPage: 1000 })
            .then(res => {
                if (res.error) {
                    setError(res.error);
                } else {
                    const mapped = res.data.map(item => ({
                        title: item.title,
                        link: item.document_url,
                        imageLink: item.image_url,
                        description: item.description,
                        publicationDate: new Date(item.publication_date),
                    }));
                    // Trier par date décroissante
                    mapped.sort((a, b) => b.publicationDate - a.publicationDate);
                    setOddData(mapped);
                }
            })
            .catch(err => {
                setError('Erreur lors du chargement des données.');
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, []);

    // Années disponibles pour le filtre
    const availableYears = useMemo(() => {
        const years = oddData.map(item => item.publicationDate.getFullYear());
        return [...new Set(years)].sort((a, b) => b - a);
    }, [oddData]);

    // Filtrage par année
    const filteredData = useMemo(() => {
        if (filterYear === 'all') return oddData;
        return oddData.filter(item => item.publicationDate.getFullYear() === parseInt(filterYear));
    }, [oddData, filterYear]);

    const handleLoadMore = () => setVisibleCount(prev => prev + 2);
    const handleFilterChange = (event) => {
        setFilterYear(event.target.value);
        setVisibleCount(2);
    };

    // Styles (reproduits de l'original)
    const cardStyle = {
        marginBottom: '10px',
        border: '1px solid #ddd',
        borderRadius: '5px',
    };
    const imageStyle = {
        width: '80px',
        height: 'auto',
        marginRight: '20px',
        borderRadius: '5px',
        display: 'block',
    };
    const contentStyle = { flex: '1' };
    const menuItemStyle = { color: 'green' };

    if (loading) {
        return (
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '10px' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                    <Typography variant="body1">Chargement des données...</Typography>
                </Container>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '10px' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                    <Typography variant="body1" color="error">{error}</Typography>
                </Container>
                <Footer />
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '10px' }}>
            <NavigationBar />
            <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                <Typography variant="h6" align="justify" gutterBottom>
                    Les objectifs de développement durable (ODD)
                </Typography>
                <Typography variant="body1" align="justify" paragraph>
                    Cette section met en lumière les rapports et documents en lien avec les Objectifs de Développement Durable (ODD). Le rapport d'actualisation des indicateurs ODD résulte des efforts collaboratifs du Comité multisectoriel dédié à la surveillance des indicateurs de mise en œuvre des programmes de développement, incluant les ODD et le Plan National de Développement (PND), tout ceci avec le soutien financier du Programme des Nations Unies pour le Développement (PNUD).
                    Cette actualisation annuelle fournit au Gouvernement des données précieuses pour ajuster efficacement sa politique en vue d'atteindre la vision 2030.
                </Typography>

                <FormControl fullWidth margin="normal" style={{ marginLeft: '0px', width: '300px' }}>
                    <InputLabel id="year-filter-odd-label">Sélectionnez par l'année de publication (ODD)</InputLabel>
                    <Select
                        labelId="year-filter-odd-label"
                        id="year-filter-odd"
                        value={filterYear}
                        label="Sélectionnez par l'année de publication (ODD)"
                        onChange={handleFilterChange}
                    >
                        <MenuItem value="all">Tous les indicateurs (ODD)</MenuItem>
                        {availableYears.map(year => (
                            <MenuItem key={year} value={year} style={menuItemStyle}>{year}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {filteredData.slice(0, visibleCount).map((item, index) => (
                    <Card key={index} style={cardStyle}>
                        <CardContent>
                            <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'flex-start', textDecoration: 'none', color: 'inherit' }}>
                                <Box>
                                    <img src={item.imageLink} alt={item.title} style={imageStyle} />
                                </Box>
                                <Box style={contentStyle}>
                                    <Typography variant="h6" component="div" style={{ color: 'green' }}>
                                        <DownloadIcon /> {item.title}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {item.description}
                                    </Typography>
                                </Box>
                            </a>
                        </CardContent>
                    </Card>
                ))}

                {visibleCount < filteredData.length && (
                    <Button
                        onClick={handleLoadMore}
                        style={{
                            marginTop: '20px',
                            backgroundColor: '#388E3C',
                            color: 'white',
                        }}
                    >
                        Plus des indicateurs (ODD)
                    </Button>
                )}
            </Container>
            <br />
            <br />
            <Footer />
        </div>
    );
}

export default ODDIndicators;